/**
 * Alarm horns. One tone pattern per ISA-18.2 priority so the priority is audible as well
 * as visible — colour is never the only signal (CLAUDE.md rule 10).
 */
type Priority = 'emergency' | 'high' | 'medium' | 'low';

interface Pattern {
  freq: number;
  /** Beeps per second; 0 = continuous. */
  rate: number;
  gain: number;
}

const PATTERNS: Record<Priority, Pattern> = {
  emergency: { freq: 880, rate: 4, gain: 0.16 },
  high: { freq: 620, rate: 2, gain: 0.12 },
  medium: { freq: 440, rate: 1, gain: 0.09 },
  low: { freq: 330, rate: 0.5, gain: 0.06 },
};

let ctx: AudioContext | null = null;
let osc: OscillatorNode | null = null;
let gain: GainNode | null = null;
let current: Priority | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let enabled = true;

function ensure(): AudioContext | null {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return null;
  if (!ctx) {
    ctx = new AudioContext();
    gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 440;
    osc.connect(gain);
    osc.start();
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function setHornEnabled(on: boolean): void {
  enabled = on;
  if (!on) stopHorn();
}

export function playHorn(priority: Priority | null): void {
  if (!enabled || priority === null) {
    stopHorn();
    return;
  }
  if (priority === current) return;
  const c = ensure();
  if (!c || !osc || !gain) return;
  current = priority;
  const p = PATTERNS[priority];
  osc.frequency.setValueAtTime(p.freq, c.currentTime);
  if (timer) clearInterval(timer);
  let on = true;
  const apply = (): void => {
    if (!gain || !ctx) return;
    gain.gain.setTargetAtTime(on ? p.gain : 0, ctx.currentTime, 0.01);
    on = !on;
  };
  apply();
  timer = setInterval(apply, 1000 / (p.rate * 2));
}

export function stopHorn(): void {
  current = null;
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (gain && ctx) gain.gain.setTargetAtTime(0, ctx.currentTime, 0.02);
}

/** Short confirmation blip for operator actions. */
export function blip(ok = true): void {
  const c = ensure();
  if (!c || !enabled) return;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = 'sine';
  o.frequency.value = ok ? 880 : 220;
  g.gain.value = 0.05;
  o.connect(g);
  g.connect(c.destination);
  o.start();
  g.gain.setTargetAtTime(0, c.currentTime + 0.04, 0.02);
  o.stop(c.currentTime + 0.16);
}
